import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  TrendingUp, 
  TrendingDown,
  Target,
  Mail,
  Users,
  Calendar,
  Download
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_TICK = { fontSize: 11, fill: '#737373' };
const CHART_AXIS = { stroke: '#E5E5E5' };

const PERIOD_OPTIONS = [
  { value: '1', label: '1 mois' },
  { value: '2', label: '2 mois' },
  { value: '3', label: '3 mois' },
  { value: '4', label: '4 mois' },
  { value: '5', label: '5 mois' },
  { value: '6', label: '6 mois' },
  { value: '9', label: '9 mois' },
  { value: '12', label: '12 mois' },
  { value: '18', label: '18 mois' },
  { value: '24', label: '24 mois' },
];

export default function Analytics() {
  const [period, setPeriod] = useState('6');
  const [selectedConsultant, setSelectedConsultant] = useState('all');
  const [selectedClient, setSelectedClient] = useState('all');
  const [consultants, setConsultants] = useState([]);
  const [clients, setClients] = useState([]);
  const [keywordsData, setKeywordsData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBaseData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAnalyticsData();
  }, [period, selectedConsultant, selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBaseData = async () => {
    try {
      const [consultantsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/consultants`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true })
      ]);
      setConsultants(consultantsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      console.warn('Failed to fetch base data:', e);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        months: period,
        ...(selectedConsultant !== 'all' && { consultant_id: selectedConsultant }),
        ...(selectedClient !== 'all' && { client_id: selectedClient })
      });

      const [keywordsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics/keywords?${params}`, { withCredentials: true }),
        axios.get(`${API}/analytics/leads?${params}`, { withCredentials: true })
      ]);

      setKeywordsData(keywordsRes.data.monthly_data || []);
      setSummaryStats(keywordsRes.data.summary || {});
      setLeadsData(leadsRes.data.monthly_data || []);
    } catch (e) {
      // Generate mock data for demo
      generateMockData();
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    const months = parseInt(period);
    const now = new Date();
    
    // Generate keywords evolution data
    const kwData = [];
    const leadsDataMock = [];
    
    for (let i = months - 1; i >= 0; i--) {
      const date = subMonths(now, i);
      const monthKey = format(date, 'MMM yyyy', { locale: fr });
      
      // Keywords data - simulated improvement over time
      const basePosition = 50 - (months - i) * 2 + Math.random() * 10;
      kwData.push({
        month: monthKey,
        avgPosition: Math.max(1, Math.round(basePosition)),
        improved: Math.floor(Math.random() * 15) + 5,
        declined: Math.floor(Math.random() * 8),
        stable: Math.floor(Math.random() * 10) + 3,
        totalKeywords: Math.floor(Math.random() * 20) + 30
      });
      
      // Leads data
      leadsDataMock.push({
        month: monthKey,
        total: Math.floor(Math.random() * 30) + 10,
        googleOrganic: Math.floor(Math.random() * 15) + 5,
        googleAds: Math.floor(Math.random() * 10) + 2,
        linkedin: Math.floor(Math.random() * 8) + 1,
        direct: Math.floor(Math.random() * 5) + 1,
        referral: Math.floor(Math.random() * 6) + 2
      });
    }
    
    setKeywordsData(kwData);
    setLeadsData(leadsDataMock);
    
    // Summary stats
    const totalImproved = kwData.reduce((sum, d) => sum + d.improved, 0);
    const totalDeclined = kwData.reduce((sum, d) => sum + d.declined, 0);
    const totalLeads = leadsDataMock.reduce((sum, d) => sum + d.total, 0);
    const avgPosition = Math.round(kwData.reduce((sum, d) => sum + d.avgPosition, 0) / kwData.length);
    
    setSummaryStats({
      totalKeywords: kwData[kwData.length - 1]?.totalKeywords || 0,
      avgPosition,
      totalImproved,
      totalDeclined,
      totalLeads,
      avgLeadsPerMonth: Math.round(totalLeads / months)
    });
  };

  const filteredClients = selectedConsultant === 'all' 
    ? clients 
    : clients.filter(c => c.consultant_id === selectedConsultant);

  const exportCSV = () => {
    const headers = [
      'Mois', 'Total leads', 'Google Organic', 'Google Ads', 'LinkedIn',
      'Referral', 'Direct', 'Position moy.', 'Mots-cles', 'Ameliores', 'En baisse',
    ];
    const rows = keywordsData.map((row) => {
      const l = leadsData.find(x => x.month === row.month) || {};
      return [
        row.month,
        l.total ?? 0,
        l.googleOrganic ?? 0,
        l.googleAds ?? 0,
        l.linkedin ?? 0,
        l.referral ?? 0,
        l.direct ?? 0,
        row.avgPosition ?? '',
        row.totalKeywords ?? '',
        row.improved ?? 0,
        row.declined ?? 0,
      ];
    });
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(';')).join('\n');
    // BOM for Excel UTF-8
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const clientLabel = selectedClient === 'all'
      ? 'tous-clients'
      : (clients.find(c => c.id === selectedClient)?.name || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `detail-mensuel_${clientLabel}_${period}mois_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-neutral-900 text-white p-3 rounded shadow-lg text-sm">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={entry.name} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Analytics
            </h1>
            <p className="text-neutral-500 mt-1">Suivi détaillé sur {period} mois</p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-neutral-500" />
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[130px]" data-testid="period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Select value={selectedConsultant} onValueChange={(v) => { setSelectedConsultant(v); setSelectedClient('all'); }}>
              <SelectTrigger className="w-[180px]" data-testid="consultant-filter">
                <SelectValue placeholder="Tous les consultants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les consultants</SelectItem>
                {consultants.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-[180px]" data-testid="client-filter">
                <SelectValue placeholder="Tous les clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {filteredClients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-neutral-200 bg-white">
          <div className="p-4 border-r border-b lg:border-b-0 border-neutral-200">
            <div className="flex items-center gap-2 text-neutral-500 mb-1">
              <Target className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">Mots-clés</span>
            </div>
            <p className="font-mono text-2xl font-bold text-neutral-900">
              {summaryStats.totalKeywords || 0}
            </p>
          </div>
          <div className="p-4 border-r border-b lg:border-b-0 border-neutral-200">
            <div className="flex items-center gap-2 text-neutral-500 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">Position moy.</span>
            </div>
            <p className="font-mono text-2xl font-bold text-neutral-900">
              {summaryStats.avgPosition || '-'}
            </p>
          </div>
          <div className="p-4 border-r border-b md:border-b-0 border-neutral-200">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">Améliorés</span>
            </div>
            <p className="font-mono text-2xl font-bold text-green-600">
              +{summaryStats.totalImproved || 0}
            </p>
          </div>
          <div className="p-4 border-r border-neutral-200">
            <div className="flex items-center gap-2 text-red-500 mb-1">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">En baisse</span>
            </div>
            <p className="font-mono text-2xl font-bold text-red-500">
              -{summaryStats.totalDeclined || 0}
            </p>
          </div>
          <div className="p-4 border-r border-neutral-200">
            <div className="flex items-center gap-2 text-neutral-500 mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">Total leads</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[#002FA7]">
              {summaryStats.totalLeads || 0}
            </p>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2 text-neutral-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">Leads/mois</span>
            </div>
            <p className="font-mono text-2xl font-bold text-neutral-900">
              {summaryStats.avgLeadsPerMonth || 0}
            </p>
          </div>
        </div>

        {/* Charts Row 1 - Keywords Evolution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Average Position Evolution */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="position-chart">
            <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
              Évolution de la position moyenne
            </h2>
            <p className="text-sm text-neutral-500 mb-4">Position moyenne des mots-clés par mois</p>
            
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={keywordsData}>
                    <defs>
                      <linearGradient id="positionGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#002FA7" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#002FA7" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis 
                      dataKey="month" 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <YAxis 
                      reversed 
                      domain={[1, 'auto']}
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="avgPosition" 
                      name="Position moyenne"
                      stroke="#002FA7" 
                      strokeWidth={2}
                      fill="url(#positionGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Keywords Movement */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="keywords-movement-chart">
            <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
              Mouvement des mots-clés
            </h2>
            <p className="text-sm text-neutral-500 mb-4">Amélioration vs baisse par mois</p>
            
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={keywordsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis 
                      dataKey="month" 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <YAxis 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="improved" name="Améliorés" fill="#22C55E" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="declined" name="En baisse" fill="#EF4444" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="stable" name="Stables" fill="#A3A3A3" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2 - Leads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Total Leads per Month */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="leads-total-chart">
            <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
              Formulaires reçus par mois
            </h2>
            <p className="text-sm text-neutral-500 mb-4">Nombre total de leads/formulaires</p>
            
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis 
                      dataKey="month" 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <YAxis 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" name="Total leads" fill="#002FA7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Leads by Source */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="leads-source-chart">
            <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
              Leads par source
            </h2>
            <p className="text-sm text-neutral-500 mb-4">Répartition par canal d'acquisition</p>
            
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={leadsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis 
                      dataKey="month" 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <YAxis 
                      tick={CHART_TICK}
                      axisLine={CHART_AXIS}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="googleOrganic" name="Google Organic" stackId="1" stroke="#22C55E" fill="#22C55E" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="googleAds" name="Google Ads" stackId="1" stroke="#EAB308" fill="#EAB308" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="linkedin" name="LinkedIn" stackId="1" stroke="#0077B5" fill="#0077B5" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="referral" name="Referral" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="direct" name="Direct" stackId="1" stroke="#737373" fill="#737373" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Detail Table */}
        <div className="bg-white border border-neutral-200" data-testid="monthly-table">
          <div className="p-4 border-b border-neutral-200 flex items-center justify-between gap-3">
            <h2 className="font-heading font-semibold text-lg text-neutral-900">
              Détail mensuel
            </h2>
            <button
              type="button"
              onClick={exportCSV}
              disabled={!keywordsData.length}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Télécharger le détail mensuel au format CSV"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th className="text-right">Position moy.</th>
                  <th className="text-right">Mots-clés</th>
                  <th className="text-right text-green-600">Améliorés</th>
                  <th className="text-right text-red-500">En baisse</th>
                  <th className="text-right">Leads</th>
                </tr>
              </thead>
              <tbody>
                {keywordsData.map((row) => {
                  const leadRow = leadsData.find(l => l.month === row.month);
                  return (
                    <tr key={row.month}>
                      <td className="font-medium">{row.month}</td>
                      <td className="text-right font-mono">{row.avgPosition}</td>
                      <td className="text-right font-mono">{row.totalKeywords}</td>
                      <td className="text-right font-mono text-green-600">+{row.improved}</td>
                      <td className="text-right font-mono text-red-500">-{row.declined}</td>
                      <td className="text-right font-mono font-bold text-[#002FA7]">
                        {leadRow?.total ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
